import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import { getDebugHelperMeetings, isNoSignInPreviewActive } from '../../debug'
import {
  projectHelperMeetings,
  type HelperMeetingItem,
  type HelperReadinessItem,
} from './helperMeetingProjection'

export type { HelperMeetingItem, HelperReadinessItem } from './helperMeetingProjection'

/**
 * Subscribes to meetings for the active workspace where the helper is in
 * `attendeeIds`. Filters in two stages: server-side by workspaceId for index
 * efficiency, then client-side by attendee membership (since `array-contains`
 * combined with the workspaceId equality + orderBy would require an additional
 * composite index that we are deferring to Phase 3).
 */
export const subscribeToHelperMeetings = (
  workspaceId: string,
  uid: string,
  onChange: (items: HelperMeetingItem[]) => void,
): Unsubscribe => {
  if (isNoSignInPreviewActive()) {
    onChange(getDebugHelperMeetings())
    return () => {}
  }
  const q = query(
    collection(firestoreDb, 'meetings'),
    where('workspaceId', '==', workspaceId),
    orderBy('scheduledAt', 'asc'),
    limit(100),
  )
  return onSnapshot(
    q,
    (snapshot) => {
      onChange(projectHelperMeetings(
        snapshot.docs.map((entry) => ({ id: entry.id, data: entry.data() as Record<string, unknown> })),
        uid,
      ))
    },
    () => onChange([]),
  )
}

export const setHelperReadinessItemDone = async (
  meetingId: string,
  index: number,
  done: boolean,
  current: HelperReadinessItem[],
): Promise<void> => {
  const next = current.map((item, i) => (i === index ? { ...item, done } : item))
  await updateDoc(doc(firestoreDb, 'meetings', meetingId), { readinessChecklist: next })
}
